import React, { createContext, useContext, useState, useCallback } from 'react';

export interface ShippingAddress {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  apartment: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export interface PaymentMethod {
  type: 'card' | 'paypal' | 'applepay';
  cardNumber?: string;
  cardName?: string;
  expiryDate?: string;
  cvv?: string;
}

export interface OrderDetails {
  orderId: string;
  orderDate: Date;
  shippingAddress: ShippingAddress;
  paymentMethod: PaymentMethod;
  subtotal: number;
  shipping: number;
  discount: number;
  total: number;
  estimatedDelivery: string;
}

interface CheckoutContextType {
  shippingAddress: ShippingAddress | null;
  paymentMethod: PaymentMethod | null;
  orderDetails: OrderDetails | null;
  promoCode: string;
  promoApplied: boolean;
  setShippingAddress: (address: ShippingAddress) => void;
  setPaymentMethod: (method: PaymentMethod) => void;
  setPromoCode: (code: string) => void;
  setPromoApplied: (applied: boolean) => void;
  completeOrder: (details: Omit<OrderDetails, 'orderId' | 'orderDate'>) => void;
  resetCheckout: () => void;
}

const CheckoutContext = createContext<CheckoutContextType | undefined>(undefined);

const generateOrderId = () => {
  return 'GG-' + Math.random().toString(36).substring(2, 8).toUpperCase();
};

export const CheckoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);

  const completeOrder = useCallback((details: Omit<OrderDetails, 'orderId' | 'orderDate'>) => {
    setOrderDetails({
      ...details,
      orderId: generateOrderId(),
      orderDate: new Date(),
    });
  }, []);

  const resetCheckout = useCallback(() => {
    setShippingAddress(null);
    setPaymentMethod(null);
    setOrderDetails(null);
    setPromoCode('');
    setPromoApplied(false);
  }, []);

  return (
    <CheckoutContext.Provider value={{
      shippingAddress,
      paymentMethod,
      orderDetails,
      promoCode,
      promoApplied,
      setShippingAddress,
      setPaymentMethod,
      setPromoCode,
      setPromoApplied,
      completeOrder,
      resetCheckout,
    }}>
      {children}
    </CheckoutContext.Provider>
  );
};

export const useCheckout = () => {
  const context = useContext(CheckoutContext);
  if (!context) throw new Error('useCheckout must be used within CheckoutProvider');
  return context;
};
